/**
 * Market Hours & Exchange Session Schedule Utility
 * 
 * Rules:
 * 1. Crypto: 24/7/365 (Always open, immune to all session/weekend closures)
 * 2. Forex: Opens Sunday 17:00 NY -> Closes Friday 17:00 NY (Closed weekends)
 * 3. US Stocks (NYSE/NASDAQ): Mon-Fri 09:30 - 16:00 America/New_York (Closed weekends & outside 9:30-16:00)
 * 4. BIST (Borsa İstanbul): Mon-Fri 10:00 - 18:00 Europe/Istanbul (Closed weekends & outside 10:00-18:00)
 * 5. European Stocks (XETR/EURONEXT): Mon-Fri 09:00 - 17:30 Europe/Berlin (Closed weekends & outside hours)
 * 6. Commodities & Indices: Opens Sunday 18:00 NY -> Closes Friday 17:00 NY (Daily break 17:00-18:00 NY, closed weekends)
 */

import { useState, useEffect } from 'react';

export const getMarketCategory = (symbol = '', category = '') => {
  const normCat = (category || '').toLowerCase();
  if (normCat === 'crypto' || normCat === 'cryptocurrency') return 'crypto';
  if (normCat === 'forex') return 'forex';
  if (normCat === 'commodities' || normCat === 'commodity') return 'commodities';
  if (normCat === 'indices' || normCat === 'index') return 'indices';
  if (normCat === 'bist' || normCat === 'stocks_bist') return 'bist';
  if (normCat === 'eu_stocks' || normCat === 'stocks_eu') return 'stocks_eu';
  if (normCat === 'stocks_us' || normCat === 'stocks') return 'stocks_us';

  const s = symbol.toUpperCase().replace('/', '').trim();
  if (s.endsWith('.IS') || s.endsWith('-IST') || s.startsWith('BIST:')) return 'bist';
  if (['BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'DOGE', 'ADA', 'AVAX', 'DOT', 'LTC', 'LINK', 'UNI', 'SHIB', 'NEAR', 'MATIC'].some(c => s.startsWith(c))) {
    return 'crypto';
  }
  if (['EUR', 'GBP', 'USD', 'JPY', 'CHF', 'AUD', 'CAD', 'NZD', 'TRY', 'ZAR', 'MXN', 'SGD'].includes(s.slice(0, 3)) && s.length === 6) {
    return 'forex';
  }
  if (symbol.includes('/')) return 'forex';
  if (['XAU', 'XAG', 'XPT', 'XPD', 'USOIL', 'UKOIL', 'WTI', 'BRENT', 'NATGAS', 'COPPER', 'CL', 'BZ', 'HG', 'NG'].some(c => s.startsWith(c) || s === c)) {
    return 'commodities';
  }
  if (['US100', 'US500', 'US30', 'NAS100', 'GER40', 'UK100', 'DAX', 'XU100', 'XU030', 'JP225', 'HK50', 'FRA40', 'EU50'].some(c => s.startsWith(c))) {
    return 'indices';
  }
  if (['.DE', '.PA', '.SW', '.AS', '.L', '.MC', '.MI'].some(ext => s.endsWith(ext))) {
    return 'stocks_eu';
  }

  return 'stocks_us';
};

/**
 * Returns localized day and decimal hour for a given IANA timeZone
 */
const getZonedTime = (timeZone) => {
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).formatToParts(now);

    const map = {};
    for (const p of parts) map[p.type] = p.value;

    const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(map.weekday);
    const hour = parseInt(map.hour, 10);
    const minute = parseInt(map.minute, 10);
    const decimalHour = hour + minute / 60;
    return { dayOfWeek, hour, minute, decimalHour };
  } catch (e) {
    // Fallback if timezone not supported
    const utcDay = now.getUTCDay();
    const utcHour = now.getUTCHours() + now.getUTCMinutes() / 60;
    return { dayOfWeek: utcDay, hour: now.getUTCHours(), minute: now.getUTCMinutes(), decimalHour: utcHour };
  }
};

/**
 * Evaluates whether the market for a given symbol is currently OPEN or CLOSED
 */
export const checkMarketHours = (symbol, category = '') => {
  const cat = getMarketCategory(symbol, category);

  // 1. CRYPTO: 100% immune, 24/7/365
  if (cat === 'crypto') {
    return {
      isOpen: true,
      status: 'open',
      reason: 'Open 24/7',
      marketName: 'Spot Crypto',
      workingHours: '24/7 / 365',
      isCrypto: true,
    };
  }

  // 2. FOREX: Sunday 17:00 NY -> Friday 17:00 NY
  if (cat === 'forex') {
    const { dayOfWeek, decimalHour } = getZonedTime('America/New_York');
    // Saturday: closed
    if (dayOfWeek === 6) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'Weekend — Forex opens Sunday 17:00 NY',
        marketName: 'Global Forex',
        workingHours: 'Sun 17:00 – Fri 17:00 EST',
        isCrypto: false,
      };
    }
    // Sunday before 17:00 NY: closed
    if (dayOfWeek === 0 && decimalHour < 17) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'Weekend — Forex opens today at 17:00 NY',
        marketName: 'Global Forex',
        workingHours: 'Sun 17:00 – Fri 17:00 EST',
        isCrypto: false,
      };
    }
    // Friday after 17:00 NY: closed
    if (dayOfWeek === 5 && decimalHour >= 17) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'Weekend — Forex closed until Sunday 17:00 NY',
        marketName: 'Global Forex',
        workingHours: 'Sun 17:00 – Fri 17:00 EST',
        isCrypto: false,
      };
    }
    return {
      isOpen: true,
      status: 'open',
      reason: 'Market Open',
      marketName: 'Global Forex',
      workingHours: 'Sun 17:00 – Fri 17:00 EST',
      isCrypto: false,
    };
  }

  // 3. COMMODITIES & INDICES: Sunday 18:00 NY -> Friday 17:00 NY
  if (cat === 'commodities' || cat === 'indices') {
    const { dayOfWeek, decimalHour } = getZonedTime('America/New_York');
    const label = cat === 'commodities' ? 'Commodities CFD' : 'Indices CFD';
    if (dayOfWeek === 6) {
      return {
        isOpen: false,
        status: 'closed',
        reason: `${label} closed for the weekend (Opens Sun 18:00 NY)`,
        marketName: label,
        workingHours: 'Sun 18:00 – Fri 17:00 EST',
        isCrypto: false,
      };
    }
    if (dayOfWeek === 0 && decimalHour < 18) {
      return {
        isOpen: false,
        status: 'closed',
        reason: `${label} opens Sunday at 18:00 NY`,
        marketName: label,
        workingHours: 'Sun 18:00 – Fri 17:00 EST',
        isCrypto: false,
      };
    }
    if (dayOfWeek === 5 && decimalHour >= 17) {
      return {
        isOpen: false,
        status: 'closed',
        reason: `${label} closed for the weekend (Opens Sun 18:00 NY)`,
        marketName: label,
        workingHours: 'Sun 18:00 – Fri 17:00 EST',
        isCrypto: false,
      };
    }
    // Daily break Mon-Thu 17:00-18:00 NY
    if (dayOfWeek >= 1 && dayOfWeek <= 4 && decimalHour >= 17 && decimalHour < 18) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'Daily Session Maintenance (17:00–18:00 NY)',
        marketName: label,
        workingHours: 'Daily break 17:00–18:00 EST',
        isCrypto: false,
      };
    }
    return {
      isOpen: true,
      status: 'open',
      reason: 'Market Open',
      marketName: label,
      workingHours: 'Sun 18:00 – Fri 17:00 EST',
      isCrypto: false,
    };
  }

  // 4. BIST (Borsa İstanbul): Mon-Fri 10:00 - 18:00 Istanbul time
  if (cat === 'bist') {
    const { dayOfWeek, decimalHour } = getZonedTime('Europe/Istanbul');
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'Borsa İstanbul closed for the weekend (Opens Monday 10:00 TSİ)',
        marketName: 'Borsa İstanbul',
        workingHours: 'Mon-Fri 10:00–18:00 TSİ',
        isCrypto: false,
      };
    }
    if (decimalHour < 10 || decimalHour >= 18) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'Borsa İstanbul outside trading hours (10:00–18:00 TSİ)',
        marketName: 'Borsa İstanbul',
        workingHours: 'Mon-Fri 10:00–18:00 TSİ',
        isCrypto: false,
      };
    }
    return {
      isOpen: true,
      status: 'open',
      reason: 'Market Open',
      marketName: 'Borsa İstanbul',
      workingHours: 'Mon-Fri 10:00–18:00 TSİ',
      isCrypto: false,
    };
  }

  // 5. EUROPEAN STOCKS: Mon-Fri 09:00 - 17:30 Berlin/Paris
  if (cat === 'stocks_eu') {
    const { dayOfWeek, decimalHour } = getZonedTime('Europe/Berlin');
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'European Equities closed for the weekend (Opens Monday 09:00 CET)',
        marketName: 'European Equities',
        workingHours: 'Mon-Fri 09:00–17:30 CET',
        isCrypto: false,
      };
    }
    if (decimalHour < 9 || decimalHour >= 17.5) {
      return {
        isOpen: false,
        status: 'closed',
        reason: 'European Equities outside trading hours (09:00–17:30 CET)',
        marketName: 'European Equities',
        workingHours: 'Mon-Fri 09:00–17:30 CET',
        isCrypto: false,
      };
    }
    return {
      isOpen: true,
      status: 'open',
      reason: 'Market Open',
      marketName: 'European Equities',
      workingHours: 'Mon-Fri 09:00–17:30 CET',
      isCrypto: false,
    };
  }

  // 6. US STOCKS (NYSE/NASDAQ): Mon-Fri 09:30 - 16:00 America/New_York
  const { dayOfWeek, decimalHour } = getZonedTime('America/New_York');
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return {
      isOpen: false,
      status: 'closed',
      reason: 'US Stock Markets closed for the weekend (Opens Monday 09:30 EST)',
      marketName: 'NYSE / NASDAQ',
      workingHours: 'Mon-Fri 09:30–16:00 EST',
      isCrypto: false,
    };
  }
  if (decimalHour < 9.5 || decimalHour >= 16) {
    return {
      isOpen: false,
      status: 'closed',
      reason: 'US Stock Markets outside trading hours (09:30–16:00 EST)',
      marketName: 'NYSE / NASDAQ',
      workingHours: 'Mon-Fri 09:30–16:00 EST',
      isCrypto: false,
    };
  }

  return {
    isOpen: true,
    status: 'open',
    reason: 'Market Open',
    marketName: 'NYSE / NASDAQ',
    workingHours: 'Mon-Fri 09:30–16:00 EST',
    isCrypto: false,
  };
};

/**
 * React hook to track real-time market open/closed status for a symbol
 */
export const useMarketStatus = (symbol, category = '') => {
  const [status, setStatus] = useState(() => checkMarketHours(symbol, category));

  useEffect(() => {
    // Initial check
    setStatus(checkMarketHours(symbol, category));

    // Re-check every 15 seconds
    const interval = setInterval(() => {
      setStatus(checkMarketHours(symbol, category));
    }, 15000);

    return () => clearInterval(interval);
  }, [symbol, category]);

  return status;
};
